import { useState } from "react";
import { CalendarCheck, Menu, Phone, X } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL } from "@/lib/campaign";
import { track } from "@/lib/track";

const NAV = (guided) => [
  { href: "#reviews", label: "Reviews" },
  { href: "#perimenopause", label: "Perimenopause" },
  { href: "#visit", label: "Your Visit" },
  { href: "#meet", label: "Meet Dr. Jill" },
  ...(guided ? [{ href: "#pricing", label: "Pricing" }] : []),
  { href: "#faq", label: "FAQs" },
];

export const Header = ({ variant, onBook, navItems }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const nav = navItems || NAV(variant === "menopause");
  return (
    <header
      data-testid="campaign-header"
      className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-[var(--brand-gold-soft)]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
        <a href="#top" className="flex items-center gap-2.5 shrink-0" aria-label="All-In Precision Wellness, Easton PA">
          <img
            data-testid="header-logo"
            src="/images/logo.png"
            alt="All-In Precision Wellness"
            className="h-12 w-12 lg:h-14 lg:w-14 object-contain"
          />
          <span data-testid="header-location-label" className="hidden xl:block text-sm text-[var(--brand-muted)]">
            Easton, PA
          </span>
        </a>
        <nav data-testid="header-nav" className="hidden lg:flex items-center gap-6" aria-label="Page sections">
          {nav.map((n) => (
            <a
              key={n.href}
              data-testid={`nav-link-${n.href.slice(1)}`}
              href={n.href}
              className="text-[15px] font-medium text-[var(--brand-navy-deep)] hover:text-[var(--brand-gold)] transition-colors whitespace-nowrap"
            >
              {n.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <a
            data-testid="header-call-button"
            href={PHONE_TEL} data-call-location="header"
            onClick={() => track(variant, "call_click")}
            className="inline-flex items-center gap-2 border-2 border-[var(--brand-navy)] text-[var(--brand-navy)] text-sm font-semibold px-3 sm:px-4 py-2 bg-white hover:bg-[var(--brand-cream)] transition-colors"
          >
            <Phone className="h-4 w-4" aria-hidden="true" />
            <span className="hidden md:inline">{PHONE_DISPLAY}</span>
            <span className="md:hidden">Call</span>
          </a>
          <button
            data-testid="header-book-button"
            onClick={onBook}
            className="hidden sm:inline-flex items-center gap-2 bg-[var(--brand-navy)] text-white text-sm font-semibold px-4 py-2.5 hover:bg-[var(--brand-navy-deep)] transition-colors whitespace-nowrap"
          >
            <CalendarCheck className="h-4 w-4" aria-hidden="true" /> Book with Dr. Crosson
          </button>
          <button
            data-testid="header-menu-button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            className="lg:hidden p-2 text-[var(--brand-navy)]"
          >
            {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>
      {menuOpen && (
        <div data-testid="header-mobile-menu" className="lg:hidden border-t border-[var(--brand-gold-soft)] bg-white px-4 pb-5 pt-2">
          <nav className="flex flex-col" aria-label="Page sections">
            {nav.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setMenuOpen(false)}
                className="py-3 text-base font-medium text-[var(--brand-navy-deep)] border-b border-[var(--brand-gold-soft)]/60 hover:text-[var(--brand-gold)]"
              >
                {n.label}
              </a>
            ))}
          </nav>
          <button
            data-testid="mobile-menu-book-button"
            onClick={() => {
              setMenuOpen(false);
              onBook();
            }}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[var(--brand-navy)] text-white text-base font-semibold py-3.5"
          >
            <CalendarCheck className="h-5 w-5" aria-hidden="true" /> Book with Dr. Crosson
          </button>
        </div>
      )}
    </header>
  );
};
